import { Box, CircularProgress, Typography } from '@mui/material';
import './ModelsCatalog.scss';
import { HomeModelsFilters } from './possibly-stateful-components/SearchFilters/HomeModelsFilters.tsx';
import { useState, useMemo } from 'react';
import { ResultHomeModels } from '../../possibly-stateful-components/Results/ResultHomeModels';
import { HomeModelsResponseData } from '../../models/homes.models';
import { useGetHomeModelFavorites } from '../../api/favorites';
import { useGetHomeModels } from '../../api/homes';
import { useTitle } from '../../hooks/useTitle.ts';

export const ModelsCatalog = () => {
  const [priceRange, setPriceRange] = useState<number[]>([0, 100]);
  const [minBathroom, setMinBathroom] = useState('');
  const [maxBathroom, setMaxBathroom] = useState('');
  const [minBedroom, setMinBedroom] = useState('');
  const [maxBedroom, setMaxBedroom] = useState('');
  const [squareFeetRange, setSquareFeetRange] = useState<number[]>([0, 100]);
  const [minStories, setMinStories] = useState('');
  const [maxStories, setMaxStories] = useState('');
  const [styleSelected, setStyleSelected] = useState('');
  const [frameTypesSelected, setFrameTypesSelected] = useState('');
  const [familyTypesSelected, setFamilyTypesSelected] = useState('');
  const [specialFeaturesSelected, setSpecialFeaturesSelected] = useState('');
  const showSpinner = false;

  const { data: homeModelsFavorites } = useGetHomeModelFavorites();
  const { data: HomeModelsData } = useGetHomeModels();

  // useEffect(() => {
  //   document.title = 'Home Models Catalog - Immersive Homes'; // Use your specific title here
  // }, []);

  useTitle('Home Models Catalog - Immersive Homes');

  const filteredHomeModels = useMemo(() => {
    if (!HomeModelsData) return [];

    const filteredModels = HomeModelsData.filter(
      (model: HomeModelsResponseData) => {
        if (
          Number(model.price_min) < priceRange[0] ||
          Number(model.price_max) > priceRange[1]
        ) {
          return false;
        }

        const minBedroomsValue =
          minBedroom === 'No Min Bedrooms' ? 1 : Number(minBedroom);
        const maxBedroomsValue =
          maxBedroom === 'No Max Bedrooms' || !maxBedroom
            ? 10
            : Number(maxBedroom);
        if (
          model.bedrooms < minBedroomsValue ||
          model.bedrooms > maxBedroomsValue
        ) {
          return false;
        }

        const minBathroomsValue =
          minBathroom === 'No Min Bathrooms' ? 1 : Number(minBathroom);
        const maxBathroomsValue =
          maxBathroom === 'No Max Bathrooms' || !maxBathroom
            ? 10
            : Number(maxBathroom);
        if (
          model.bathrooms < minBathroomsValue ||
          model.bathrooms > maxBathroomsValue
        ) {
          return false;
        }

        if (
          model.square_feet < squareFeetRange[0] ||
          model.square_feet > squareFeetRange[1]
        ) {
          return false;
        }

        const minStoriesValue =
          minStories === 'No Min Stories' ? 1 : Number(minStories);
        const maxStoriesValue =
          maxStories === 'No Max Stories' || !maxStories
            ? 10
            : Number(maxStories);
        if (
          Number(model.stories) < minStoriesValue ||
          Number(model.stories) > maxStoriesValue
        ) {
          return false;
        }

        if (
          styleSelected &&
          model?.style?.name !== styleSelected &&
          styleSelected !== '0'
        ) {
          return false;
        }

        if (
          frameTypesSelected &&
          model?.framing_type !== frameTypesSelected &&
          frameTypesSelected !== '0'
        ) {
          return false;
        }

        if (
          familyTypesSelected &&
          model.single_or_multi !== familyTypesSelected &&
          familyTypesSelected !== '0'
        ) {
          return false;
        }

        if (
          specialFeaturesSelected &&
          model.feature?.name !== specialFeaturesSelected &&
          specialFeaturesSelected !== '0'
        ) {
          return false;
        }

        return true;
      }
    );

    return filteredModels;
  }, [
    HomeModelsData,
    minBathroom,
    maxBathroom,
    minBedroom,
    maxBedroom,
    minStories,
    maxStories,
    styleSelected,
    frameTypesSelected,
    familyTypesSelected,
    specialFeaturesSelected,
    priceRange,
    squareFeetRange,
  ]);

  const resultsList = () => {
    return (
      <Box data-testid="models-catalog" className="models-catalog">
        <HomeModelsFilters
          priceRange={priceRange}
          setPriceRange={setPriceRange}
          minBathroom={minBathroom}
          setMinBathroom={setMinBathroom}
          maxBathroom={maxBathroom}
          setMaxBathroom={setMaxBathroom}
          minBedroom={minBedroom}
          setMinBedroom={setMinBedroom}
          maxBedroom={maxBedroom}
          setMaxBedroom={setMaxBedroom}
          minStories={minStories}
          setMinStories={setMinStories}
          maxStories={maxStories}
          setMaxStories={setMaxStories}
          squareFeetRange={squareFeetRange}
          setSquareFeetRange={setSquareFeetRange}
          styleSelected={styleSelected}
          setStyleSelected={setStyleSelected}
          frameTypesSelected={frameTypesSelected}
          setFrameTypesSelected={setFrameTypesSelected}
          familyTypesSelected={familyTypesSelected}
          setFamilyTypesSelected={setFamilyTypesSelected}
          specialFeaturesSelected={specialFeaturesSelected}
          setSpecialFeaturesSelected={setSpecialFeaturesSelected}
        />
        <Box className="content">
          <Box className="header">
            <Typography variant="h1">Home Models</Typography>
            <Box className="subhead-container">
              <Typography className="text">
                Pricing estimates include home build, installation, site prep
                and permitting, but do not include land costs. They also vary
                based on hurricane wind loads
              </Typography>
            </Box>
          </Box>
          {filteredHomeModels.length > 0 ? (
            <Box className="results-list-home-model">
              {filteredHomeModels.map((result: HomeModelsResponseData) => (
                <Box key={result.id} sx={{ maxWidth: '350px' }}>
                  <ResultHomeModels
                    homeModelFavorites={homeModelsFavorites}
                    result={result}
                  />
                </Box>
              ))}
            </Box>
          ) : (
            <Box className="no-results">
              <Typography>No Home Models found</Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <>
      {showSpinner ? (
        <Box className="loading">
          <CircularProgress sx={{ marginRight: '12px' }} />
          <Typography>Loading...</Typography>{' '}
        </Box>
      ) : (
        resultsList()
      )}
    </>
  );
};
